import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useMemo, useState } from "react";

import { faExclamationCircle } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck, faCirclePlus, faSparkles, faXmark } from "@performyard/fontawesome/pro-solid-svg-icons";
import { Avatar, ButtonStandard, Icon, LogoLoader, Text, TextDate } from "@performyard/py-components";
import type {
	PostMeetingAnalysisMissedCommitment,
	PostMeetingAnalysisResponseStreamJson,
	PostMeetingAnalysisSettings,
} from "@performyard/service-contracts";

import { InsightsGeneratedAt } from "../../../../components/AIModules/InsightsGeneratedAt";
import IconButton from "../../../../components/Button/IconButton";
import { CopyToClipboard } from "../../../../components/CopyToClipboard";
import IconComponent from "../../../../components/Icon/Icon";
import Panel from "../../../../components/Panel";

import { useSuppressAskPerformYard } from "../../../../containers/AskPerformYard/hooks/useSuppressAskPerformYard";

import { useAddMeetingActionItem } from "../../../../api/hooks";
import type { usePostMeetingAnalysis } from "../hooks/usePostMeetingAnalysis";

import { formatMeetingDateTimeDisplay } from "../utils/dateFormatters";
import {
	type AnalysisNamesByRole,
	formatAnalysisAsPlainText,
	groupDecisionsByOwner,
	parseCommitmentDueDate,
	toActionItemDueDate,
	toFirstName,
} from "../utils/formatAnalysisAsPlainText";
import { isNoDataPlaceholder } from "../utils/formatPrepAsPlainText";
import { getParticipantName } from "../utils/participants";

import { POST_MEETING_ANALYSIS_CONFIG } from "../config";
import { useMeetingContext } from "../context";
import { meetingDetailsQueryKeys } from "../query-keys";

type PostMeetingAnalysisPanelProps = ReturnType<typeof usePostMeetingAnalysis>;

const { sectionTitleMap, loadingMessages, noDecisionsText, noMissedCommitmentsText, generalDecisionsLabel } =
	POST_MEETING_ANALYSIS_CONFIG;

/**
 * "Add Action Item" text button matching Figma design:
 * [+ icon] Add Action Item in primary blue, right-aligned; flips to green "Added" once created
 */
const AddActionItemButton = ({
	onClick,
	isAdded,
	disabled,
}: {
	onClick: () => void;
	isAdded: boolean;
	disabled: boolean;
}) => {
	const isInactive = isAdded || disabled;
	return (
		<button
			type="button"
			onClick={isInactive ? undefined : onClick}
			disabled={isInactive}
			data-testid="post-meeting-analysis-add-action-item-button"
			className={`flex items-center gap-1 self-end border-none bg-transparent p-0 outline-hidden ${isInactive ? "cursor-default" : "cursor-pointer hover:opacity-80"}`}
		>
			<Icon icon={isAdded ? faCircleCheck : faCirclePlus} size={16} color={isAdded ? "green-5" : "primary-5"} />
			<Text size="paragraph-3" weight="semi-bold" color={isAdded ? "green-5" : "primary-5"}>
				{isAdded ? "Added" : "Add Action Item"}
			</Text>
		</button>
	);
};

/**
 * Missed commitment card matching Figma: bordered card with assignee avatar,
 * commitment description, due date, and an Add Action Item button that creates
 * a real meeting action item.
 */
const MissedCommitmentCard = ({
	itemKey,
	commitment,
	assigneeId,
	assigneeName,
	meetingId,
	onAdded,
	isAdded,
}: {
	itemKey: string;
	commitment: PostMeetingAnalysisMissedCommitment;
	assigneeId?: string;
	assigneeName: string;
	meetingId: string;
	onAdded: (key: string) => void;
	isAdded: boolean;
}) => {
	const { mutate: addActionItem } = useAddMeetingActionItem();
	const queryClient = useQueryClient();
	// Per-card in-flight guard so a double-click can't create duplicate action items
	const [isAdding, setIsAdding] = useState(false);
	const dueDate = parseCommitmentDueDate(commitment.dueDate);
	// null when the due date is missing or in the past, matching createActionItem's rejection rule so the
	// displayed date and the stored date agree; a past date is hidden and saved as null.
	const actionItemDueDate = toActionItemDueDate(commitment.dueDate);

	const handleAdd = useCallback(() => {
		setIsAdding(true);
		addActionItem(
			{
				data: {
					name: commitment.description,
					meetings: [{ meetingId, status: "incomplete" }],
					participants: assigneeId ? [{ userId: assigneeId, role: "assignee" }] : [],
					dueDate: actionItemDueDate,
				},
			},
			{
				onSuccess: async () => {
					await queryClient.invalidateQueries({
						queryKey: meetingDetailsQueryKeys.meetingActionItems(meetingId),
					});
					onAdded(itemKey);
				},
				onError: () => setIsAdding(false),
			}
		);
	}, [actionItemDueDate, addActionItem, assigneeId, commitment, itemKey, meetingId, onAdded, queryClient]);

	return (
		<div className="flex w-full flex-col gap-1">
			<div className="box-border flex max-w-full min-w-0 flex-col gap-2 rounded-lg border border-solid border-neutral-3 bg-neutral-1 p-3">
				<div className="flex w-full items-start gap-2">
					<Avatar size={24} id={assigneeId} alt={assigneeName} />
					<div className="flex min-w-0 flex-1 flex-col gap-1">
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
							{commitment.description}
						</Text>
						{dueDate && actionItemDueDate && (
							<div className="flex items-center gap-1">
								<Text size="extra-small" weight="regular" color="neutral-9">
									Due Date:
								</Text>
								<TextDate
									date={dueDate}
									hasTime
									hasDelimiter
									dateSize="medium"
									size="extra-small"
									weight="regular"
									color="neutral-9"
								/>
							</div>
						)}
					</div>
				</div>
			</div>
			<AddActionItemButton onClick={handleAdd} isAdded={isAdded} disabled={isAdding} />
		</div>
	);
};

/**
 * Analysis section with a semi-bold heading followed by its content, matching Figma.
 */
const AnalysisSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
	<div className="flex flex-col gap-2">
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
			{title}:
		</Text>
		{children}
	</div>
);

const AnalysisText = ({ children }: { children: React.ReactNode }) => (
	<Text size="paragraph-3" weight="regular" color="neutral-9" dangerouslySetClassName="whitespace-pre-wrap">
		{children}
	</Text>
);

/**
 * Panel header: PerformYard branding + close button + meeting name card.
 * Shared between loading, content, and error states per Figma.
 */
const PanelHeader = ({
	meetingName,
	meetingDate,
	onClose,
	showMeetingCard = true,
}: {
	meetingName?: string;
	meetingDate?: string;
	onClose: () => void;
	showMeetingCard?: boolean;
}) => (
	<div className="flex flex-col gap-1 px-4 pt-4">
		<div className="flex w-full items-center justify-between">
			<div className="flex items-center gap-1 rounded p-1">
				<IconComponent icon={faSparkles} color="neutral-9" size="small" />
				<Text size="paragraph-2" weight="regular" color="neutral-9">
					PerformYard
				</Text>
			</div>
			<IconButton
				testId="post-meeting-analysis-close-button"
				icon={faXmark}
				onClick={onClose}
				aria-label="Close panel"
			/>
		</div>
		{showMeetingCard && meetingName && (
			<div className="border-b border-solid border-neutral-2 pb-4">
				<div className="flex flex-col gap-2 rounded bg-neutral-1 p-2">
					<Text size="paragraph-1" weight="semi-bold" color="neutral-9">
						{meetingName}
					</Text>
					{meetingDate && (
						<div className="flex items-center gap-1">
							<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
								Meeting Date:
							</Text>
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								{meetingDate}
							</Text>
						</div>
					)}
				</div>
			</div>
		)}
	</div>
);

const PostMeetingAnalysisPanel = ({
	data,
	isLoading,
	error,
	isOpen,
	handleClose,
	handleRegenerate,
	directReportId,
	postMeetingAnalysisSettings,
}: PostMeetingAnalysisPanelProps): React.ReactElement => {
	const { meeting, userId, workspace, participantNames } = useMeetingContext();
	const [addedItems, setAddedItems] = useState<Set<string>>(new Set());

	// The LLM attributes work by role; we resolve each role to a real name for display and copy.
	const namesByRole = useMemo<AnalysisNamesByRole>(
		() => ({
			manager: getParticipantName(workspace, participantNames, userId) || "Manager",
			direct_report: getParticipantName(workspace, participantNames, directReportId) || "Direct Report",
		}),
		[workspace, participantNames, userId, directReportId]
	);

	// Hide the Ask PerformYard FAB while the panel is open so it doesn't overlap the footer actions.
	useSuppressAskPerformYard(isOpen);

	const formattedMeetingDate = useMemo(() => {
		if (!meeting?.startDateTime || !meeting?.endDateTime) return undefined;
		const { dateStr, timeStr, isMultiDay } = formatMeetingDateTimeDisplay(
			new Date(meeting.startDateTime),
			new Date(meeting.endDateTime)
		);
		return isMultiDay ? dateStr : `${dateStr} • ${timeStr}`;
	}, [meeting?.startDateTime, meeting?.endDateTime]);

	const handleItemAdded = useCallback((key: string) => {
		setAddedItems((prev) => new Set(prev).add(key));
	}, []);

	const resolveAssigneeId = useCallback(
		(assigneeRole: PostMeetingAnalysisMissedCommitment["assigneeRole"]): string | undefined => {
			if (assigneeRole === "manager") return userId;
			if (assigneeRole === "direct_report") return directReportId;
			return undefined;
		},
		[userId, directReportId]
	);

	const resolveAssigneeName = useCallback(
		(assigneeRole: PostMeetingAnalysisMissedCommitment["assigneeRole"]): string =>
			assigneeRole === null ? "Unassigned" : namesByRole[assigneeRole],
		[namesByRole]
	);

	const analysisData = data?.data as PostMeetingAnalysisResponseStreamJson | undefined;

	// A cached analysis can contain sections an admin has since disabled — hide those (Meeting Overview is always shown)
	const isSectionEnabled = useCallback(
		(key: keyof PostMeetingAnalysisSettings): boolean => postMeetingAnalysisSettings?.[key] ?? true,
		[postMeetingAnalysisSettings]
	);

	const clipboardText = useMemo(() => {
		if (!analysisData) return "";
		return formatAnalysisAsPlainText(
			analysisData,
			{ meetingName: meeting?.name ?? "", meetingDate: formattedMeetingDate ?? "", namesByRole },
			postMeetingAnalysisSettings
		);
	}, [analysisData, postMeetingAnalysisSettings, meeting?.name, formattedMeetingDate, namesByRole]);

	return (
		<Panel side="right" isOpen={isOpen} onClose={handleClose} width={416}>
			{error ? (
				/* ===== ERROR STATE (Figma: header + centered error + footer with Retry/Cancel) ===== */
				<div className="flex h-full flex-col">
					<PanelHeader onClose={handleClose} showMeetingCard={false} />
					<div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
						<div className="mb-2 aspect-square w-fit rounded-full bg-red-1 p-1">
							<Icon icon={faExclamationCircle} color="red-5" size={24} />
						</div>
						<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
							The generation of Post Meeting Analysis was unsuccessful. Please try again.
						</Text>
					</div>
					<div className="flex items-center gap-2 border-t border-solid border-neutral-2 px-4 py-4">
						<div className="flex h-8 flex-1 items-center">
							<ButtonStandard variant="solid" color="primary" size="medium" onClick={handleRegenerate}>
								Retry
							</ButtonStandard>
						</div>
						<div className="flex h-8 flex-1 items-center">
							<ButtonStandard variant="outline" color="neutral" size="medium" onClick={handleClose}>
								Cancel
							</ButtonStandard>
						</div>
					</div>
				</div>
			) : isLoading ? (
				/* ===== LOADING STATE (Figma: header + meeting card + centered loader) ===== */
				<div className="flex h-full flex-col">
					<PanelHeader meetingName={meeting?.name} meetingDate={formattedMeetingDate} onClose={handleClose} />
					<div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
						<LogoLoader
							textArray={loadingMessages}
							mainText="Generating Meeting Analysis"
							switchTimer={2500}
						/>
					</div>
				</div>
			) : analysisData ? (
				/* ===== CONTENT STATE ===== */
				<div className="flex h-full flex-col gap-4">
					{/* Sticky Header */}
					<div className="sticky top-0 z-10 bg-white">
						<PanelHeader
							meetingName={meeting?.name}
							meetingDate={formattedMeetingDate}
							onClose={handleClose}
						/>
					</div>

					{/* Scrollable Content */}
					<div className="flex flex-1 flex-col gap-2 overflow-y-auto px-6">
						{/* Info section */}
						<InsightsGeneratedAt label="Insights Populated" date={data?.createdAt} />

						<div className="flex flex-col gap-4">
							{/* Title */}
							<div className="flex items-start gap-1">
								<IconComponent icon={faSparkles} color="primary-5" size="small" />
								<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
									Meeting Analysis
								</Text>
							</div>

							{/* Meeting Overview (always present) */}
							{analysisData.meetingOverview && (
								<AnalysisSection title={sectionTitleMap.meetingOverview}>
									<AnalysisText>{analysisData.meetingOverview}</AnalysisText>
								</AnalysisSection>
							)}

							{/* Decisions (grouped by owner per Figma) */}
							{analysisData.decisions && isSectionEnabled("decisionsMade") && (
								<AnalysisSection title={sectionTitleMap.decisions}>
									{analysisData.decisions.length === 0 ? (
										<AnalysisText>{noDecisionsText}</AnalysisText>
									) : (
										groupDecisionsByOwner(analysisData.decisions).map((group) => (
											<div key={group.ownerRole ?? "none"} className="flex flex-col gap-2">
												<Text size="paragraph-3" weight="regular" color="neutral-9">
													{group.ownerRole
														? `${toFirstName(namesByRole[group.ownerRole])}'s Decisions`
														: generalDecisionsLabel}
												</Text>
												<ul className="m-0 list-disc space-y-2 pl-6 text-neutral-9 marker:text-xs">
													{group.decisions.map((decision, index) => (
														<li key={index} className="text-sm leading-4 font-normal">
															<span className="text-sm leading-4 font-normal whitespace-pre-wrap">
																{decision}
															</span>
														</li>
													))}
												</ul>
											</div>
										))
									)}
								</AnalysisSection>
							)}

							{/* Missed Commitments (interactive) */}
							{analysisData.missedCommitments && isSectionEnabled("missedCommitments") && (
								<div className="flex flex-col gap-4">
									<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
										{sectionTitleMap.missedCommitments}
									</Text>
									{analysisData.missedCommitments.length === 0 ? (
										<AnalysisText>{noMissedCommitmentsText}</AnalysisText>
									) : (
										<div className="flex min-w-0 flex-col gap-2 rounded-lg bg-neutral-1 p-2">
											{analysisData.missedCommitments.map((commitment, index) => {
												const itemKey = `${commitment.description}-${index}`;
												return (
													<MissedCommitmentCard
														key={itemKey}
														itemKey={itemKey}
														commitment={commitment}
														assigneeId={resolveAssigneeId(commitment.assigneeRole)}
														assigneeName={resolveAssigneeName(commitment.assigneeRole)}
														meetingId={meeting._id}
														onAdded={handleItemAdded}
														isAdded={addedItems.has(itemKey)}
													/>
												);
											})}
										</div>
									)}
								</div>
							)}

							{/* Participation & Inclusion (omit when the model returned a no-data placeholder) */}
							{analysisData.participationAndInclusion &&
								isSectionEnabled("participationAndInclusion") &&
								!isNoDataPlaceholder(analysisData.participationAndInclusion) && (
									<AnalysisSection title={sectionTitleMap.participationAndInclusion}>
										<AnalysisText>{analysisData.participationAndInclusion}</AnalysisText>
									</AnalysisSection>
								)}

							{/* Quality of Discussion vs. Monologue (omit when the model returned a no-data placeholder) */}
							{analysisData.qualityOfDiscussion &&
								isSectionEnabled("qualityOfDiscussion") &&
								!isNoDataPlaceholder(analysisData.qualityOfDiscussion) && (
									<AnalysisSection title={sectionTitleMap.qualityOfDiscussion}>
										<AnalysisText>{analysisData.qualityOfDiscussion}</AnalysisText>
									</AnalysisSection>
								)}

							{/* Time Management (omit when the model returned a no-data placeholder) */}
							{analysisData.timeManagement &&
								isSectionEnabled("timeManagement") &&
								!isNoDataPlaceholder(analysisData.timeManagement) && (
									<AnalysisSection title={sectionTitleMap.timeManagement}>
										<AnalysisText>{analysisData.timeManagement}</AnalysisText>
									</AnalysisSection>
								)}

							{/* Meeting Coaching */}
							{analysisData.meetingCoaching && isSectionEnabled("meetingCoaching") && (
								<AnalysisSection title={sectionTitleMap.meetingCoaching}>
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										{sectionTitleMap.topStrength}
									</Text>
									<AnalysisText>{analysisData.meetingCoaching.topStrength}</AnalysisText>
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										{sectionTitleMap.primaryDevelopmentArea}
									</Text>
									<AnalysisText>{analysisData.meetingCoaching.primaryDevelopmentArea}</AnalysisText>
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										{sectionTitleMap.oneThingToTryNextMeeting}
									</Text>
									<AnalysisText>{analysisData.meetingCoaching.oneThingToTryNextMeeting}</AnalysisText>
								</AnalysisSection>
							)}
						</div>
					</div>

					{/* Footer */}
					<div className="sticky bottom-0 flex items-center gap-2 border-t border-solid border-neutral-2 bg-white px-4 py-4">
						<div className="flex h-8 flex-1 items-center">
							<ButtonStandard
								variant="solid"
								color="primary"
								size="medium"
								iconLeft={faSparkles}
								onClick={handleRegenerate}
								disabled={isLoading}
							>
								Regenerate
							</ButtonStandard>
						</div>
						{isLoading ? (
							<div className="flex h-8 flex-1 items-center">
								<ButtonStandard variant="outline" color="neutral" size="medium" disabled>
									Copy to Clipboard
								</ButtonStandard>
							</div>
						) : (
							<CopyToClipboard text={clipboardText}>
								<div className="flex h-8 flex-1 items-center">
									<ButtonStandard variant="outline" color="neutral" size="medium">
										Copy to Clipboard
									</ButtonStandard>
								</div>
							</CopyToClipboard>
						)}
					</div>
				</div>
			) : null}
		</Panel>
	);
};

export default PostMeetingAnalysisPanel;
